import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { SceneConfig } from '../types/sceneConfig';
import SceneViewer from '../components/SceneViewer';
import { I18nProvider } from '../i18n';
import { S3_BUCKET_NAME, S3_REGION } from '../config/env';

// ─────────────────────────────────────────────────────────────────────────────
// Single-scene camera defaults (world-space).
//
// TODO: move these into the DB as defaultCameraPos / defaultLookAt fields,
// then delete this block.
// ─────────────────────────────────────────────────────────────────────────────

const SINGLE_SCENE_CAMERA: Record<string, {
  pos?: [number, number, number];
  lookAt?: [number, number, number];
  reconstructionId?: string;
  siSessionName?: string;
}> = {
  'wingwalker':  { pos: [-38.32, 37.13, 14.82] },
  'vexcel-14':   { pos: [1.92, 61.96, -45.77],  lookAt: [-2.9, 56.15, -39.21] },
  'vexcel-18':   { pos: [1.92, 61.96, -45.77],  lookAt: [50.56, 53.56, -18.09] },
  'via-boxed':   { pos: [18.02, 1.9, 6.25],     lookAt: [8.09, 1.25, 5.34] },
  'via-complete':{ pos: [18.02, 1.9, 6.25],     lookAt: [8.09, 1.25, 5.34] },
  'allisonroad': {
    pos: [1638.017, 92.323, -3018.730],
    lookAt: [1633.610, 92.323, 2999.226],
    reconstructionId: '719ff930-c878-49ab-a559-9caf21f8389a',
  },
  'apartment':   {
    pos: [-1.237, -0.035, 1.599],
    lookAt: [-5.720, -1.163, -0.222],
    reconstructionId: '2ad91042-f797-43a4-a5d8-53cf4b9bb238',
  },
  'mosaic':      { pos: [0, 8, 18],              lookAt: [0, 0, 0] },
  'prairie-1':   {
    pos: [-13.64, 11.52, -16.29],
    lookAt: [-8.2, 4.44, -11.8],
  },
  'prairie-2':   {
    pos: [-2.76, 11.52, -7.31],
    lookAt: [-8.2, 4.44, -11.8],
    reconstructionId: '1cde4005-72cf-4fcc-90e3-2ea310f7a3f0',
  },
  'terrebonne-segment': {
    pos: [-1714.12, 56.90, 822.43],
    lookAt: [-1725.54, 56.85, 832.58],
    reconstructionId: 'fc297651-9fae-48f1-87d3-fe1aa979d64e',
    siSessionName: 'terrebonne_power_line',
  },
  'terrebonne_roads': {
    pos: [-1685.73, 57.54, 840.11],
    lookAt: [-1651.97, 57.54, 852.79],
  },
  'terrebonne streets': {
    pos: [-1685.73, 57.54, 840.11],
    lookAt: [-1651.97, 57.54, 852.79],
  },
  'dhanas_house': {
    pos: [-1.50, 0.41, -10.32],
    lookAt: [-1.50, 0.41, 0],
    reconstructionId: 'b2c43051-e634-4fac-8710-63ec3d859f5d',
    siSessionName: 'dhanas_house',
  },
  'holt_homes': {
    pos: [-944.59, 275.04, 844.26],
    reconstructionId: '0a2b7675-5607-4b80-85ff-0adc55d29ae7',
    siSessionName: 'holt_homes_jan_2026',
  },
  'holt_homes_jan_2026': {
    pos: [-944.59, 275.04, 844.26],
    siSessionName: 'holt_homes_jan_2026',
  },
  'drone_construction_site': {
    pos: [50.005, 41.921, -7.230],
    lookAt: [50.499, 6.551, -61.738],
    siSessionName: 'holt_homes_jan_2026',
  },
  'aerial_construction_site': {
    pos: [9.130, 35.830, 42.850],
    lookAt: [9.130, 35.830, -575.492],
  },
  'home_construction': {
    pos: [-1.50, 0.41, -10.32],
    lookAt: [-1.50, 0.41, 0],
  },
  'city_segment': {
    pos: [-1715.1094287552594, 58.061667415452334, 822.1945422993398],
    lookAt: [-6640, 34.3, 5200],
  },
  'spexi': {
    pos: [-83.06, 100.92, -30.26],
    lookAt: [-84.45, 91.02, -30.52],
  },
  'stadium_zemun': {
    pos: [-2657.179, 31.163, 613.543],
    lookAt: [-2643.655, 17.092, 594.765],
    siSessionName: 'stadium_zemun',
    siContextNote: `Stadium Zemun seat detection data (from top-down floor plan analysis):
- 4,633 detected seats across 108 sections (section 01 – section 108).
- Seats are tagged by three dimensions: Section, Seat color, and Direction (compass stand).
- Stand breakdown: west 1,180 · south-east 1,115 · north-east 991 · south-west 872 · south 269 · north-west 105 · north 72 · east 29.
- Color breakdown: mint 1,114 · light blue/blue 1,035 · light blue 754 · green 697 · mint/white/pale light blue 659 · dark blue 374.
- Largest sections: section 83 (1,052), section 59 (515), section 12 (502), section 81 (495), section 65 (411).
- Smallest sections: several sections have only 1 detected seat (sections 97, 98, 102, 104, 106).
- Seat naming: each pin is named S<section>-<index> e.g. S83-0001.
- Each seat and each section has a pre-computed camera viewpoint (observer) that the viewer can teleport to.
- When asked to navigate to a section or stand, use action navigateToPin with pinSectionName matching the section tag (e.g. "section 83") or a stand direction (e.g. "north", "west").
- When asked to highlight / show / filter seats by group, use action filterPins with pinFilters mapping group name to tag value. Group names are "Section", "Seat color", "Direction". E.g. {"Direction": "north"} or {"Section": "section 83"}.
- The total number of detected seats (4,633) is the detected count. The stadium's designed capacity is not stored in this dataset — do not invent a capacity figure; instead say the dataset contains 4,633 detected seats and the designed capacity is unknown from this data.`,
  },
  'montreal_2': { pos: [-2352.858, 23.012, 487.973], lookAt: [-2352.858, 23.012, 464.527] },
  'amsterdam':  { pos: [-850.206, 74.577, 1719.722], lookAt: [-842.798, 12.653, 1802.195] },
  'tallin':      { pos: [-515.655, 77.214, 362.353], lookAt: [-545.728, 24.764, 406.091] },
  'tallin_july': { pos: [-658.71, 82.66, 431.74],   lookAt: [-671.68, 22.07, 345.98] },
  // Repointed for full_lafayette_culled.rad, whose geometry sits near
  // (265, 0, 693). The previous values belonged to vexcel-lafayette.rad,
  // ~3,000 units away — see the note in MarketingGallery.
  'lafayette':   { pos: [265, 450, 1200], lookAt: [265, 60, 693] },
  'lafayette_overlay': {
    pos: [338.704, 1543.804, 1522.584],
    lookAt: [338.689, 1543.287, 1522.905],
  },
  'lafayette overlay': {
    pos: [338.704, 1543.804, 1522.584],
    lookAt: [338.689, 1543.287, 1522.905],
  },
  'tallin_twin':      { pos: [116.967, 93.125, 170.642],     lookAt: [71.029, 54.572, 171.616] },
  'amsterdam_center':    { pos: [-2296.02, 40.35, 2099.95],      lookAt: [-2297.09, 10.06, 2061.47] },
  'amsterdam_2024_no_hg':  { pos: [-853.155, 55.612, 1766.843],  lookAt: [-879.771, 23.355, 1725.765] },
  'lafayette_2025_east':   { pos: [212.054, 277.061, 882.716],   lookAt: [151.074, 224.692, 826.972] },
  'amsterdam_square':      {},
  'amsterdam_residential': { pos: [-2278.65, 53.03, 2166.85], lookAt: [-2296.01, -5.91, 2239.25] },
  'warriors_way_sf': {
    pos: [-2398.85, 251.16, 4070.53],
    lookAt: [-2267.23, 45.52, 3870.58],
  },
  'warriors way, san francisco': {
    pos: [-2398.85, 251.16, 4070.53],
    lookAt: [-2267.23, 45.52, 3870.58],
  },
  'warrior_way_sf_squared': {
    pos: [-2496.3, 215.6, 3849.7],
    lookAt: [-2374.6, 60.2, 3769.1],
  },
  'warrior way sf squared': {
    pos: [-2496.3, 215.6, 3849.7],
    lookAt: [-2374.6, 60.2, 3769.1],
  },
};

type RawSceneConfigSource = Partial<SceneConfig> & {
  _id?: string;
  category?: string;
  sceneDescription?: string;
};

function validCameraTuple(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length !== 3 || !value.every(Number.isFinite)) return undefined;
  return value as [number, number, number];
}

// ─────────────────────────────────────────────────────────────────────────────
// toSceneConfig
// Converts any raw API scene object to SceneConfig.
// Loading mode is always decided by manifest presence — not by which API found it.
// ─────────────────────────────────────────────────────────────────────────────

function toSceneConfig(apiScene: RawSceneConfigSource): SceneConfig {
  const resolvedSceneId = apiScene.sceneId ?? apiScene.sceneName ?? apiScene._id;

  if (!resolvedSceneId) {
    throw new Error('Scene is missing a sceneId or sceneName');
  }

  const camera = [apiScene.sceneId, apiScene.sceneName, apiScene._id]
    .filter((key): key is string => typeof key === 'string' && key.length > 0)
    .map(key => SINGLE_SCENE_CAMERA[key.toLowerCase()])
    .find(Boolean);
  const databaseCameraPos = validCameraTuple(apiScene.defaultCameraPos);
  const databaseLookAt = validCameraTuple(apiScene.defaultLookAt);

  return {
    sceneId:   resolvedSceneId,
    sceneName: apiScene.sceneName ?? 'Untitled',
    category:  apiScene.category ?? undefined,
    reconstructionId: apiScene.reconstructionId ?? camera?.reconstructionId,
    siSessionName: apiScene.siSessionName ?? camera?.siSessionName,

    // ── Loading URLs — manifest presence decides the mode in SceneViewer ──
    manifest: apiScene.manifest ?? undefined,
    gaus:     apiScene.gaus     ?? undefined,
    pinsUrl:  apiScene.pinsUrl  ?? undefined,
    inspectionUrl: apiScene.inspectionUrl ?? undefined,

    // ── Segmented camera defaults (geodetic) ─────────────────────────────
    defaultLat:     apiScene.defaultLat     ?? undefined,
    defaultLon:     apiScene.defaultLon     ?? undefined,
    defaultAlt:     apiScene.defaultAlt     ?? 1.5,
    defaultHeading: apiScene.defaultHeading ?? 0,

    // ── Single-scene camera defaults (world-space) ───────────────────────
    // Prefer DB fields; fall back to local lookup until DB is updated.
    defaultCameraPos: databaseCameraPos ?? camera?.pos,
    defaultLookAt:    databaseLookAt    ?? camera?.lookAt,

    // ── Capability signals ───────────────────────────────────────────────
    pathNetworkUrl: apiScene.pathNetworkUrl ?? undefined,
    imageBasePath:  apiScene.imageBasePath  ?? undefined,
    sceneType:      apiScene.sceneType ?? undefined,
    hasGeoAlignment: apiScene.hasGeoAlignment ?? undefined,
    supportsRecording: apiScene.supportsRecording ?? false,

    // ── Metadata ─────────────────────────────────────────────────────────
    audio:       apiScene.audio       ?? undefined,
    description: apiScene.description ?? apiScene.sceneDescription ?? undefined,
    loadingDescription: apiScene.loadingDescription ?? apiScene.description ?? apiScene.sceneDescription ?? undefined,
    thumbnail:   apiScene.thumbnail   ?? undefined,

    // ── Single-scene extras passed through to addSplatScene ──────────────
    envMap:      apiScene.envMap      ?? undefined,
    imagePoses:  apiScene.imagePoses  ?? undefined,
    coordinateFrame: (apiScene as any).coordinateFrame ?? undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SceneViewerPage
//
// Routable at /viewer?scene=<sceneId>
//
// Fetch order:
//   1. searchCollectiv3Scenes  — match by sceneId or sceneName
//   2. If not found → getGeoAutomationSceneBySceneId  (GeoAutomation scenes)
//
// Loading mode (decided inside SceneViewer via inferLoadingMode):
//   manifest present → segmented  (addSplatScenesFromManifest, geodetic coords)
//   gaus present     → single     (addSplatScene, world-space coords)
//
// Shareable links:
//   /viewer?scene=wingwalker
//   /viewer?scene=terrebonne
//   /viewer?scene=terrebonne&lat=45.711&lon=-73.638&alt=58.8&heading=90
// ─────────────────────────────────────────────────────────────────────────────

const SceneViewerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();

  const sceneId      = searchParams.get('scene');
  const gausOverride = searchParams.get('gausOverride');
  const camPosParam  = searchParams.get('camPos');
  const lookAtParam  = searchParams.get('lookAt');
  const fovParam     = searchParams.get('fov');

  const [sceneConfig, setSceneConfig] = useState<SceneConfig | null>(null);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    if (!sceneId) {
      setError('Missing required URL parameter: scene');
      return;
    }

    let cancelled = false;

    const fetchScene = async () => {
      try {
        setError(null);
        setSceneConfig(null);

        const res = await customerApi.searchCollectiv3Scenes(sceneId);
        const sceneKey = sceneId.toLowerCase();
        const match = res.success
          ? (res.data as Collectiv3Scene[] | undefined)?.find(
              (s) => s.sceneName.toLowerCase() === sceneKey || s.sceneId?.toLowerCase() === sceneKey
            )
          : undefined;
        if (!match) throw new Error(`Scene "${sceneId}" not found`);
        const resolved = toSceneConfig(match);

        if (gausOverride) {
          const overrideUrl = gausOverride.startsWith('http')
            ? gausOverride
            : `https://${S3_BUCKET_NAME}.s3.${S3_REGION}.amazonaws.com/${gausOverride}`;
          resolved.gaus = overrideUrl;
          resolved.manifest = undefined;
        }

        if (camPosParam) {
          const parts = camPosParam.split(',').map(Number);
          if (parts.length === 3 && parts.every(isFinite)) {
            resolved.defaultCameraPos = parts as [number, number, number];
          }
        }
        if (lookAtParam) {
          const parts = lookAtParam.split(',').map(Number);
          if (parts.length === 3 && parts.every(isFinite)) {
            resolved.defaultLookAt = parts as [number, number, number];
          }
        }
        if (fovParam) {
          const fov = Number(fovParam);
          if (isFinite(fov) && fov > 0 && fov < 180) resolved.cameraFov = fov;
        }

        if (cancelled) return;
        setSceneConfig(resolved);

      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load scene');
        }
      }
    };

    fetchScene();
    return () => { cancelled = true; };
  }, [sceneId, gausOverride, camPosParam, lookAtParam]);

  // ── Back / Image View handler ────────────────────────────────────────────
  const handleBack = (
    gpsPosition: { latitude: number; longitude: number; altitude: number; heading: number } | null
  ) => {
    if (window.parent !== window) {
      // Embedded in an iframe — post position back to parent
      window.parent.postMessage(
        { type: 'BACK_TO_2D', data: { gpsPosition, sceneId } },
        '*'
      );
    } else {
      // Standalone — navigate back
      navigate(-1);
    }
  };

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-[9999]">
        <div className="text-center text-white max-w-md px-8">
          <h2 className="text-2xl font-semibold mb-4">Error</h2>
          <p className="text-white/70 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-[#0C5484] text-white px-6 py-3 rounded-lg hover:bg-[#0CB4E4]/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Fetching ─────────────────────────────────────────────────────────────
  if (!sceneConfig) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0CB4E4]" />
      </div>
    );
  }

  return (
    <I18nProvider>
      <SceneViewer scene={sceneConfig} onBack={handleBack} isPopupMode={!!gausOverride} />
    </I18nProvider>
  );
};

export default SceneViewerPage;
